import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  FormControlLabel,
  Switch,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material'
import CopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import ApproveIcon from '@mui/icons-material/CheckCircle'
import UnapprovedIcon from '@mui/icons-material/Clear'

// Hooks
import { useExecutiveReports, ReportType } from '../hooks/useExecutiveReports'

// Components
import { ExecutivePillarEnhanced } from '../components/executive/ExecutivePillarEnhanced'
import PlatinumTableWrapper from '../components/platinum-table-new/PlatinumTableWrapper'
import { IndividualAnalysesModal } from '../components/executive/IndividualAnalysesModal'
import { BrandFunnelReport } from '../components/executive/BrandFunnelReport'
import useAccount from '../hooks/useAccount'
import AssessmentIcon from '@mui/icons-material/Assessment'

export const ExecutiveReports: React.FC = () => {
  // Get account info to check current job
  const account = useAccount()
  
  // State for individual analyses modal
  const [analysesModalOpen, setAnalysesModalOpen] = useState(false)
  const [analysesModalData, setAnalysesModalData] = useState<{
    sectionTitle: string
    tablesUsed: string[]
    individualAnalyses: Record<string, any>
  } | null>(null)
  
  // Use the executive reports hook
  const {
    copySuccess,
    selectedTab,
    categoriesData,
    reportType,
    brandFunnelData,
    brandFunnelLoading,
    brandFunnelError,
    isApproved,
    isTableModalOpen,
    modalTableName,
    modalIsFullscreen,
    modalIsTextWrapped,
    modalIsSearchVisible,
    modalIsSideExpanded,
    runsLoading,
    runsError,
    latestRunSummary,
    runProgress,
    runHistory,
    currentCategory,
    currentReportData,
    setCopySuccess,
    setIsApproved,
    handleCopyToClipboard,
    handleEditSection,
    copyFullSection,
    handleTabChange,
    handleReportTypeChange,
    loadTableInModal,
    handleModalClose,
    setModalIsFullscreen,
    setModalIsTextWrapped,
    setModalIsSearchVisible,
    setModalIsSideExpanded,
    refreshRunData,
  } = useExecutiveReports()
  
  // Handle opening individual analyses modal
  const handleOpenAnalysesModal = (sectionData: any) => {
    if (sectionData.tablesUsed && sectionData.individualAnalyses) {
      setAnalysesModalData({
        sectionTitle: sectionData.title,
        tablesUsed: sectionData.tablesUsed,
        individualAnalyses: sectionData.individualAnalyses
      })
      setAnalysesModalOpen(true)
    }
  }
  
  const handleCloseAnalysesModal = () => {
    setAnalysesModalOpen(false)
    setAnalysesModalData(null)
  }

  const handleReportTypeSelectChange = (event: SelectChangeEvent<ReportType>) => {
    handleReportTypeChange(event.target.value as ReportType)
  }

  const formatTimestamp = (value?: string) => {
    if (!value) {
      return '—'
    }
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return value
    }
    return parsed.toLocaleString()
  }

  const latestRun = latestRunSummary?.run
  const latestConfig = latestRunSummary?.config
  const adminCanManage = account.user?.domain_permissions?.canAccessAdmin

  // Check if current job is supported for executive reports
  const supportedJobs = [
    'sky-uk-be-prius', 'UK Prius', 
    'eire-sky-brand-equity', 'Ireland Brand Equity',
    '21066-joiners', 'NOW Joiners',
    '21066-leavers', 'NOW Leavers',
    'sky-protect', 'Sky Protect',
    '24129-fanstrack-womens-football',
  ]
  
  const isJobSupported = account.job && (
    (account.job.name && supportedJobs.includes(account.job.name)) || 
    (account.job.displayName && supportedJobs.includes(account.job.displayName))
  )
  
  // If not on a supported job, show empty state
  if (!isJobSupported) {
    return (
      <Box sx={{ 
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <AssessmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" gutterBottom color="text.primary">
          No Executive Reports Available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
          Executive reports are not yet available for this job. Please contact your data team if you need executive report generation for this job.
        </Typography>
      </Box>
    )
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {adminCanManage && (
            <Button variant='outlined' size='small' onClick={() => account.openPage('executive-config')}>
              Manage Config
            </Button>
          )}
          <Button variant='outlined' size='small' onClick={refreshRunData} disabled={runsLoading}>
            {runsLoading ? 'Refreshing...' : 'Refresh Status'}
          </Button>
        </Box>
      </Box>

      {runsError && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {runsError}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Typography variant='h6' gutterBottom>
          Latest Executive Report Run
        </Typography>
        {runsLoading && !latestRun ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant='body2'>Loading run data…</Typography>
          </Box>
        ) : latestRun ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant='body1'>Version v{latestRun.version}</Typography>
            <Typography variant='body2'>Status: {latestRun.status}</Typography>
            <Typography variant='body2'>Triggered by: {latestRun.triggeredBy || '—'}</Typography>
            <Typography variant='body2'>Requested: {formatTimestamp(latestRun.createdAt)}</Typography>
            <Typography variant='body2'>Completed: {formatTimestamp(latestRun.completedAt)}</Typography>
            {runProgress && (
              <Typography variant='body2' color='text.secondary'>
                Sections {runProgress.completedSections}/{runProgress.totalSections} · Tables {runProgress.completedTables}/{runProgress.totalTables}
              </Typography>
            )}
            {latestConfig?.label && (
              <Typography variant='body2' color='text.secondary'>
                Config label: {latestConfig.label}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            No runs have been generated yet for this job.
          </Typography>
        )}

        {runHistory.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              Run History
            </Typography>
            {runHistory.slice(0, 5).map(run => (
              <Box
                key={run.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  fontSize: 13,
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                <Typography variant='body2'>v{run.version}</Typography>
                <Typography variant='body2'>{run.status}</Typography>
                <Typography variant='body2'>{formatTimestamp(run.completedAt || run.createdAt)}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper> */}

      {/* Report Type Selector - COMMENTED OUT FOR DEMO */}
      {/* <Box sx={{ mb: 3 }}>
        <FormControl size="medium" sx={{ minWidth: 300 }}>
          <InputLabel id="report-type-label">Report Type</InputLabel>
          <Select
            labelId="report-type-label"
            id="report-type-select"
            value={reportType}
            label="Report Type"
            onChange={handleReportTypeSelectChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.info.main,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'info.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'info.main',
              },
            }}
          >
            <MenuItem value="brand-funnel">
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Brand Funnel Executive Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unified brand funnel performance across all Sky products
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem value="category-reports">
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Category Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Individual executive summaries by product category
                </Typography>
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box> */}

      {/* Brand Funnel Report */}
      {reportType === 'brand-funnel' && (
        <>
          {/* Loading State */}
          {brandFunnelLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading Brand Funnel Executive Report...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {brandFunnelError && !brandFunnelLoading && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {brandFunnelError}
              </Typography>
            </Alert>
          )}

          {/* Brand Funnel Content */}
          {brandFunnelData && !brandFunnelLoading && (
            <BrandFunnelReport
              data={brandFunnelData}
              onCopy={handleCopyToClipboard}
              onOpenTable={loadTableInModal}
            />
          )}
        </>
      )}

      {/* Category Reports */}
      {reportType === 'category-reports' && (
        <>
          {/* Category Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={selectedTab} 
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#FFC000',
                  height: '2px'
                }
              }}
            >
              {categoriesData.map((category) => (
                <Tab key={category.id} label={category.label} />
              ))}
            </Tabs>
          </Box>

          {/* Loading State */}
          {currentCategory && currentCategory.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading executive summary...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {currentCategory && currentCategory.error && !currentCategory.loading && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {currentCategory.error}
              </Typography>
            </Alert>
          )}

          {/* Content - only render if we have data */}
          {currentReportData && currentCategory && !currentCategory.loading && (
            <>
              {/* Executive Summary Section */}
              <Paper elevation={2} sx={{ mb: 4, overflow: 'hidden' }}>
                <Box 
                  sx={{ 
                    bgcolor: isApproved ? 'success.main' : 'info.main', 
                    color: 'white', 
                    p: 2.5,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {(() => {
                      // Try to get the category name from the new structure first
                      if (currentReportData.categories && currentCategory?.id && currentReportData.categories[currentCategory.id]?.name) {
                        return `${currentReportData.categories[currentCategory.id].name} - Executive Summary`
                      }
                      // Fall back to metadata fields for legacy structure
                      return `${currentReportData.metadata.categoryDisplayName || currentReportData.metadata.category || currentCategory?.label || 'Executive'} - Executive Summary`
                    })()}
                  </Typography>
                  {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isApproved}
                          onChange={(e) => setIsApproved(e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: 'white',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '& .MuiSwitch-track': {
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '110px' }}>
                          {isApproved ? (
                            <ApproveIcon fontSize="small" />
                          ) : (
                            <UnapprovedIcon fontSize="small" />
                          )}
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            {isApproved ? 'Approved' : 'Unapproved'}
                          </Typography>
                        </Box>
                      }
                      labelPlacement="start"
                      sx={{ margin: 0, color: 'white' }}
                    />
                    <Tooltip title="Copy entire summary">
                      <IconButton 
                        size="small" 
                        onClick={copyFullSection}
                        sx={{ color: 'white' }}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Box> */}
                </Box>
                
                <Box sx={{ p: 6 }}>
                  {(() => {
                    // Handle both legacy and new category-based structures
                    const pillarsData = currentReportData.pillars || 
                      (currentReportData.categories && currentCategory?.id && 
                       currentReportData.categories[currentCategory.id]?.pillars) ||
                      null
                    
                    if (pillarsData) {
                      // Filter out pillars without content
                      const pillarsWithContent = []
                      if (pillarsData.pillar1 && (pillarsData.pillar1.summary?.content || (pillarsData.pillar1.summary?.sections && pillarsData.pillar1.summary.sections.length > 0))) {
                        pillarsWithContent.push({
                          key: 'pillar1',
                          title: pillarsData.pillar1.summary?.title || pillarsData.pillar1.name || 'Brand Funnel',
                          content: pillarsData.pillar1.summary?.content || '',
                          sections: pillarsData.pillar1.summary?.sections
                        })
                      }
                      if (pillarsData.pillar2 && (pillarsData.pillar2.summary?.content || (pillarsData.pillar2.summary?.sections && pillarsData.pillar2.summary.sections.length > 0))) {
                        pillarsWithContent.push({
                          key: 'pillar2',
                          title: pillarsData.pillar2.summary?.title || pillarsData.pillar2.name || 'Brand Attributes',
                          content: pillarsData.pillar2.summary?.content || '',
                          sections: pillarsData.pillar2.summary?.sections
                        })
                      }
                      if (pillarsData.pillar3 && (pillarsData.pillar3.summary?.content || (pillarsData.pillar3.summary?.sections && pillarsData.pillar3.summary.sections.length > 0))) {
                        pillarsWithContent.push({
                          key: 'pillar3',
                          title: pillarsData.pillar3.summary?.title || pillarsData.pillar3.name || 'Competitor Analysis',
                          content: pillarsData.pillar3.summary?.content || '',
                          sections: pillarsData.pillar3.summary?.sections
                        })
                      }

                      // Determine grid columns based on number of pillars with content
                      const gridColumns = pillarsWithContent.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'

                      return (
                        <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 3 }}>
                          {pillarsWithContent.map((pillar) => (
                            <ExecutivePillarEnhanced
                              key={pillar.key}
                              title={pillar.title}
                              content={pillar.content}
                              sections={pillar.sections}
                              onCopy={handleCopyToClipboard}
                              onEdit={handleEditSection}
                              loadTableInModal={loadTableInModal}
                              onOpenAnalysesModal={handleOpenAnalysesModal}
                            />
                          ))}
                        </Box>
                      )
                    } else {
                      return (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
                          <ExecutivePillarEnhanced
                            title="Brand Funnel"
                            content="No data available"
                            onCopy={handleCopyToClipboard}
                            onEdit={handleEditSection}
                            loadTableInModal={loadTableInModal}
                          />
                          <ExecutivePillarEnhanced
                            title="Brand Attributes"
                            content="No data available"
                            onCopy={handleCopyToClipboard}
                            onEdit={handleEditSection}
                            loadTableInModal={loadTableInModal}
                          />
                          <ExecutivePillarEnhanced
                            title="Competitor Analysis"
                            content="No data available"
                            onCopy={handleCopyToClipboard}
                            onEdit={handleEditSection}
                            loadTableInModal={loadTableInModal}
                          />
                        </Box>
                      )
                    }
                  })()}
                </Box>
              </Paper>

              {/* Report Generation Info */}
              {/* <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary">
                  Generated: {new Date(currentReportData.metadata.generatedAt).toLocaleString()} • 
                  Based on {currentReportData.metadata.totalTables || 0} tables with {currentReportData.metadata.analysisCount || 0} analyses • 
                  Processing: {currentReportData.metadata.processingMode}
                </Typography>
              </Paper> */}
            </>
          )}
        </>
      )}

      {/* Table Modal */}
      <Dialog
        open={isTableModalOpen}
        onClose={handleModalClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '90%',
            height: '85%',
            maxWidth: 'none',
            maxHeight: 'none',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {modalTableName || 'Table'}
            </Typography>
            <IconButton
              onClick={handleModalClose}
              sx={{ color: 'grey.500' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
          }}>
            <PlatinumTableWrapper
              customer={account.customer?.name || ''}
              job={account.job?.name || ''}
              tablePath={modalTableName || ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Analyses Modal */}
      {analysesModalData && (
        <IndividualAnalysesModal
          open={analysesModalOpen}
          onClose={handleCloseAnalysesModal}
          sectionTitle={analysesModalData.sectionTitle}
          tablesUsed={analysesModalData.tablesUsed}
          individualAnalyses={analysesModalData.individualAnalyses}
          onOpenTable={loadTableInModal}
        />
      )}

      {/* Success snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setCopySuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Content copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ExecutiveReports
